import { storagePathToUrl } from '@talkofthetown/shared';
import { config } from '../config';

export function imageUrl(path: string): string {
  return storagePathToUrl(
    config.firebase.storageBucket,
    path,
    config.useEmulators ? `${config.emulatorHost}:9199` : null,
  );
}

export interface PreparedImage {
  full: Blob;
  thumb: Blob;
  ext: 'webp' | 'jpg';
  contentType: 'image/webp' | 'image/jpeg';
  w: number;
  h: number;
}

const FULL_MAX = 1600;
const THUMB_MAX = 640;
const MAX_BYTES = 1024 * 1024 - 1;

async function encode(
  source: ImageBitmap,
  maxDim: number,
  quality: number,
): Promise<{ blob: Blob; w: number; h: number }> {
  const scale = Math.min(1, maxDim / Math.max(source.width, source.height));
  const w = Math.max(1, Math.round(source.width * scale));
  const h = Math.max(1, Math.round(source.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not available');
  ctx.drawImage(source, 0, 0, w, h);
  const toBlob = (type: string) =>
    new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
  // Safari can't encode WebP and silently returns PNG; fall back to JPEG there.
  let blob = await toBlob('image/webp');
  if (!blob || blob.type !== 'image/webp') blob = await toBlob('image/jpeg');
  if (!blob) throw new Error('Could not process this image');
  return { blob, w, h };
}

/**
 * Resizes and re-encodes an image in the browser before upload: much smaller files (cheaper and
 * faster for everyone) and no EXIF/location metadata leaks from the original photo.
 */
export async function prepareImage(file: File): Promise<PreparedImage> {
  if (!file.type.startsWith('image/')) throw new Error(`${file.name} is not an image`);
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  try {
    let full = await encode(bitmap, FULL_MAX, 0.82);
    for (const [dim, q] of [
      [FULL_MAX, 0.7],
      [1200, 0.7],
      [900, 0.65],
    ] as const) {
      if (full.blob.size <= MAX_BYTES) break;
      full = await encode(bitmap, dim, q);
    }
    if (full.blob.size > MAX_BYTES)
      throw new Error(`${file.name} is too large even after compression`);
    const thumb = await encode(bitmap, THUMB_MAX, 0.75);
    const webp = full.blob.type === 'image/webp';
    return {
      full: full.blob,
      thumb: thumb.blob,
      ext: webp ? 'webp' : 'jpg',
      contentType: webp ? 'image/webp' : 'image/jpeg',
      w: full.w,
      h: full.h,
    };
  } finally {
    bitmap.close();
  }
}

/** Random folder name for one post's images, matching storage.rules ([A-Za-z0-9_-]{8,64}). */
export function newPostKey(): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}
